import { supabase } from '@/integrations/supabase/client';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { anonSupabase } from '@/integrations/supabase/anonClient';
import { PRIVACY_NOTICE_VERSION } from '@/pages/PrivacyPolicy';
import type { Json } from '@/integrations/supabase/types';

const ANON_SENTINEL_USER_ID = '00000000-0000-0000-0000-000000000000';
const ALLOWED_ANON_TOUCHPOINTS = new Set(['public_apply', 'interview_start', 'seek_signup']);

function toJson(value: unknown): Json {
  if (value === null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map(toJson);
  if (typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, toJson(item)]));
  }
  return String(value);
}

/**
 * Logs a consent acknowledgement for the current privacy notice version.
 *
 * @param touchpoint  Where consent was captured (e.g. 'seek_signup', 'public_apply', 'interview_start', 'cv_scoring', 'seek_cv_parse', 'cv_review', 'cv_generate', 'seek_apply')
 * @param userId      The user's auth UUID (null for anonymous flows)
 * @param portal      Which supabase client to use: 'hire' | 'seek' | 'anon'
 * @param metadata    Optional extra context
 */
export async function logConsentAcknowledgement(
  touchpoint: string,
  userId: string | null,
  portal: 'hire' | 'seek' | 'anon' = 'anon',
  metadata?: Record<string, unknown>,
) {
  const portalClient = portal === 'seek' ? seekSupabase : supabase;
  let resolvedUserId = userId;

  if (!resolvedUserId && portal !== 'anon') {
    const { data: { user } } = await portalClient.auth.getUser();
    resolvedUserId = user?.id ?? null;
  }

  if (!resolvedUserId && !ALLOWED_ANON_TOUCHPOINTS.has(touchpoint)) {
    console.warn('[consent] Anonymous acknowledgement blocked for unsupported touchpoint:', touchpoint);
    return;
  }

  const client = resolvedUserId ? portalClient : anonSupabase;

  // Get the current notice version ID
  const { data: noticeVersion } = await client
    .from('privacy_notice_versions')
    .select('id')
    .eq('version_code', PRIVACY_NOTICE_VERSION)
    .limit(1)
    .single();

  if (!noticeVersion) {
    console.warn('[consent] No privacy notice version found for', PRIVACY_NOTICE_VERSION);
    return;
  }

  const row = {
    user_id: resolvedUserId ?? ANON_SENTINEL_USER_ID,
    notice_version_id: noticeVersion.id,
    consent_type: 'privacy_policy',
    touchpoint,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent?.slice(0, 512) : null,
    metadata_json: toJson(metadata ?? {}),
  };

  const { error } = await client
    .from('consent_acknowledgements')
    .insert(row);

  if (error) {
    console.warn('[consent] Failed to log acknowledgement:', error.message);
  }
}
